import React from 'react'

import { getLocale } from '@common'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Link from 'next/link'

import { FAQModule } from '@/components/FAQModule'
import { FeatureModule } from '@/components/FeatureModule/FeatureModule'
import { FillerModule } from '@/components/FillerModule'
import { PageMetaTags } from '@/components/MetaTags'
import { Page } from '@/components/Page'
import { useProducts } from '@/features/common/useProducts'
import { GameSelector } from '@/features/landing/GameSelector'
import Hero from '@/features/landing/Hero'
import ProductList from '@/features/products/ProductList'

import { api } from '../api'
import { Button } from '../components/buttons/Button'
import { Loader } from '../components/Loader'
import { wrapGetServerSideProps, setPublicPageCacheControl } from '../ssr/wrapper'
import { isOffer } from '../utils/products'
import { COLOR_YELLOW } from '../utils/styles'
import { useAppSelector } from '../utils/useAppSelector'

import DonateOrKeep from 'assets/images/characters/donate-or-keep.png'

import styles from './landingpage.module.scss'

export default function Home() {
  const { products, isError, isLoading } = useProducts()
  const gamesOfUser = useAppSelector(
    (state) => new Set(state.user.profile?.applications?.map((a) => a.application) ?? []),
  )

  const allFeatures = products.filter((a) => isOffer(a))

  const featuredProducts = products
    .filter((a) => isOffer(a))
    .filter((a) => gamesOfUser.has(a.game))
    .concat(allFeatures)
    .slice(0, 2)

  const t = useTranslations()

  const noProducts = products.length === 0

  return (
    <Page>
      <PageMetaTags title={t('sitename')} description={t('home_description')} />
      <Head>
        <title>{t('sitename')}</title>
      </Head>
      <Hero />
      <GameSelector />
      {featuredProducts.length > 0 && (
        <FeatureModule className={styles.FeatureContentWidth} title={t('lan_sec_deals')} products={featuredProducts} />
      )}
      {isLoading && noProducts && (
        <div className={styles.LoaderContainer}>
          <Loader strokeColor={COLOR_YELLOW} />
        </div>
      )}
      {isError && noProducts && (
        <div className={styles.ErrorContainer}>
          <strong>{t('game_no_products_title')}</strong>
          <br />
          <Button type="OutlineSecondary" onClick={() => window.location.reload()}>
            {t('game_no_products_cta')}
          </Button>
        </div>
      )}
      <FillerModule
        className={styles.FeatureContentWidth}
        title={t('lan_callout1_title')}
        image={DonateOrKeep}
        action={{ content: t('lan_hero_button6'), link: '/help#guide' }}
        showPoweredBy
      >
        <span className={styles.Mobile}>
          {t.rich('lan_callout2_body', {
            TextButton: (children) => (
              <span className={styles.ReadMore}>
                <Link href="/help#guide">{children?.toString()}</Link>
              </span>
            ),
          })}
        </span>
        <span className={styles.Desktop}>{t('lan_callout1_body')}</span>
      </FillerModule>
      <ProductList products={products} />
      <FAQModule faqIds={[1, 3, 5]} />
    </Page>
  )
}

export const getServerSideProps = wrapGetServerSideProps(async (store, context, isRecognized) => {
  setPublicPageCacheControl(context, 30, 60)
  const locale = getLocale(context)
  const viewerCountry = store.getState().config.viewerCountry
  if (viewerCountry) {
    store.dispatch(api.endpoints.fetchMarket.initiate(viewerCountry))
  }
  if (!isRecognized) {
    // Fetch products for only anonymous users
    store.dispatch(api.endpoints.fetchProducts.initiate({ locale, viewerCountry }))
    setPublicPageCacheControl(context, 300, 120)
  }

  await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()))
  return { props: {} }
}, 'storeInitSSR')
